import { useEffect, useState } from "react";
import { api } from "../api";

// DB stores UTC; convert to local time for display
function fmtDate(utcStr: string): string {
  if (!utcStr) return "";
  // D1 returns "2026-05-15 11:04:05" (no Z), treat as UTC
  const d = new Date(utcStr.replace(" ", "T") + "Z");
  return d.toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const OP_LABEL: Record<string, string> = {
  translate: "Dil çoklama",
  resize: "Boyut alma",
  video: "Videolaştırma",
};

export function Queue({ productId }: { productId: string }) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [editJobs, setEditJobs] = useState<any[]>([]);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    async function tick() {
      try {
        const [r1, r2] = await Promise.all([
          api.listJobs(productId),
          api.listEditJobs(productId).catch(() => ({ edit_jobs: [] })),
        ]);
        if (!stop) {
          setJobs(r1.jobs);
          setEditJobs(r2.edit_jobs);
          setFetchError(null);
          setLoading(false);
        }
      } catch (e) {
        if (!stop) {
          setFetchError((e as Error).message);
          setLoading(false);
        }
      }
    }
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [productId]);

  async function handleCancel(jobId: string, isEdit = false) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      if (isEdit) {
        await api.cancelEditJob(jobId);
        setEditJobs((prev) =>
          prev.map((j) => (j.id === jobId ? { ...j, status: "cancelled" } : j)),
        );
      } else {
        await api.cancelJob(jobId);
        setJobs((prev) =>
          prev.map((j) => (j.id === jobId ? { ...j, status: "cancelled" } : j)),
        );
      }
    } catch (e) {
      alert("İptal edilemedi: " + (e as Error).message);
    } finally {
      setCancelling(null);
    }
  }

  return (
    <div className="p-8 space-y-8">
      {fetchError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          Yükleme hatası: {fetchError}
        </div>
      )}

      {/* Edit Jobs — shown first so video jobs are immediately visible */}
      <section>
        <h2 className="text-lg font-bold mb-4">Edit Kuyruğu</h2>
        {loading ? (
          <div className="text-zinc-500 text-sm">Yükleniyor…</div>
        ) : editJobs.length === 0 ? (
          <div className="text-zinc-500 text-sm">Henüz edit job yok.</div>
        ) : (
          <table className="w-full text-sm bg-white border rounded">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">İşlem</th>
                <th className="px-3 py-2">Kodlar</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Cost</th>
                <th className="px-3 py-2">Oluşturulma</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {editJobs.map((j) => {
                const codes: string[] = JSON.parse(j.codes_json ?? "[]");
                return (
                  <tr key={j.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{j.id.slice(0, 8)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
                        {OP_LABEL[j.operation] ?? j.operation}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-500">
                      {codes.slice(0, 3).join(", ")}{codes.length > 3 ? ` +${codes.length - 3}` : ""}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="px-3 py-2">${Number(j.cost_usd ?? 0).toFixed(4)}</td>
                    <td className="px-3 py-2 text-zinc-500">{fmtDate(j.created_at)}</td>
                    <td className="px-3 py-2">
                      {(j.status === "queued" || j.status === "running") && (
                        <button
                          className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-40"
                          disabled={cancelling === j.id}
                          onClick={() => handleCancel(j.id, true)}
                        >
                          {cancelling === j.id ? "durduruluyor…" : "durdur"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Production Jobs */}
      <section>
      <h2 className="text-lg font-bold mb-4">Üretim Kuyruğu</h2>
      {loading ? (
        <div className="text-zinc-500 text-sm">Yükleniyor…</div>
      ) : jobs.length === 0 ? (
        <div className="text-zinc-500 text-sm">Henüz job yok.</div>
      ) : (
        <table className="w-full text-sm bg-white border rounded">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-3 py-2">ID</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Cost</th>
              <th className="px-3 py-2">Oluşturulma</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{j.id.slice(0, 8)}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={j.status} />
                </td>
                <td className="px-3 py-2">${Number(j.cost_usd ?? 0).toFixed(4)}</td>
                <td className="px-3 py-2 text-zinc-500">{fmtDate(j.created_at)}</td>
                <td className="px-3 py-2 flex items-center gap-2">
                  <button
                    className="text-xs text-accent hover:underline"
                    onClick={() => setOpenJobId(j.id)}
                  >
                    detay
                  </button>
                  {(j.status === "queued" || j.status === "running") && (
                    <button
                      className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-40"
                      disabled={cancelling === j.id}
                      onClick={() => handleCancel(j.id)}
                    >
                      {cancelling === j.id ? "durduruluyor…" : "durdur"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </section>


      {openJobId && (
        <JobDetail jobId={openJobId} onClose={() => setOpenJobId(null)} />
      )}
    </div>
  );
}

function JobDetail({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    let stop = false;
    async function tick() {
      const r = await api.getJob(jobId);
      if (!stop) setData(r);
    }
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [jobId]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <div className="text-xs font-mono text-zinc-500">{jobId}</div>
            <div className="text-lg font-bold mt-0.5">Job Detail</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900">
            ✕
          </button>
        </div>
        {!data ? (
          <div className="p-8 text-zinc-500">Yükleniyor…</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex gap-4 text-sm">
              <div>
                <span className="text-zinc-500">Status: </span>
                <StatusBadge status={data.job.status} />
              </div>
              <div>
                <span className="text-zinc-500">Toplam: </span>
                <span className="font-mono">${Number(data.job.cost_usd ?? 0).toFixed(4)}</span>
              </div>
              <div>
                <span className="text-zinc-500">Medya: </span>
                {data.media.length}
              </div>
              <div>
                <span className="text-zinc-500">Dosya: </span>
                {data.files.length}
              </div>
            </div>

            <section>
              <h3 className="font-semibold mb-2">Maliyet kalemleri</h3>
              <table className="w-full text-sm border rounded">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    <th className="px-2 py-1">Provider</th>
                    <th className="px-2 py-1">Kind</th>
                    <th className="px-2 py-1 text-right">Units</th>
                    <th className="px-2 py-1 text-right">USD</th>
                  </tr>
                </thead>
                <tbody>
                  {data.costs.map((c: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1">{c.provider}</td>
                      <td className="px-2 py-1">{c.kind}</td>
                      <td className="px-2 py-1 text-right font-mono">{c.units}</td>
                      <td className="px-2 py-1 text-right font-mono">
                        ${Number(c.usd).toFixed(5)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section>
              <h3 className="font-semibold mb-2">Medya ({data.media.length})</h3>
              <ul className="space-y-2 text-sm">
                {data.media.map((m: any) => (
                  <li key={m.id} className="border rounded p-2">
                    <div className="font-mono text-xs text-zinc-500">{m.code}</div>
                    <div>{m.angle}</div>
                    <div className="text-xs text-zinc-500">
                      {m.theme} · {m.type} · {m.stage}
                    </div>
                    <div className="mt-1 text-xs">
                      {data.files.filter((f: any) => f.media_id === m.id).length} dosya
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {data.job.error && (
              <section>
                <h3 className="font-semibold mb-2 text-red-600">Hata</h3>
                <pre className="text-xs bg-red-50 p-3 rounded overflow-auto">
                  {data.job.error}
                </pre>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued: "bg-zinc-100 text-zinc-700",
    running: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700",
    cancelled: "bg-orange-100 text-orange-700",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${map[status] ?? "bg-zinc-100"}`}>
      {status}
    </span>
  );
}
